import React, { useRef, useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { useFetchKolList } from "features/metadesk/redux/hooks";
import { useOutletContext } from "react-router-dom";
import { Button, COLOR, SIZE } from "@metacrm/metacrm-material-ui/dist/Button";
import { useDispatch } from "react-redux";
import { StyledFilterBtn } from "./KolTable.styles";
import TableMenu from "components/CustomTable/TableMenu";
import { InputField } from "@metacrm/metacrm-material-ui/dist/InputField";
import _ from "lodash";
import { useSelector } from "react-redux";
import { selectRemindBarShow } from "../../redux/globalState/globalState.selector";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import useGetKolTable from "../hooks/useGetKolTable";
import FilterModal from "./FilterModal";
import { isObject } from "lodash";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";
import FilterList from "./FilterList";
import useNavigateEntity from "hooks/useNavigateEntity";
import { useIntl } from "react-intl";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import CreateKolGroupModal from "./CreateKolGroupModal";
import AddKolToGroupModal from "./AddKolToGroupModal";
import Back from "features/metadesk/components/Back";
import axios from "axios";
import { apiUrl } from "features/configure";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";

const KolTable = ({
  pageTitleKey,
  pageDescriptionKey,
  groupData,
  showControls = {
    filter: true,
    search: true,
  },
}) => {
  const { formatMessage } = useIntl();
  const [entityName] = useOutletContext();
  const remindBarShow = useSelector(selectRemindBarShow);
  const dispatchSnackbar = useDispatchSnackbar();
  const confirm = useConfirm();
  const dispatch = useDispatch();

  const [openModal, setOpenModal] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRowKolData, setSelectedRowKolData] = useState([]);

  const ModalType = {
    FILTER: "filter",
    CREATE_GROUP: "createKolGroup",
    ADD_KOL: "addKoltoGroup",
  };

  const filteredShowControls = {
    ...showControls,
    title: pageTitleKey !== undefined,
    description: pageDescriptionKey !== undefined,
  };

  const {
    kolListFilter: filter,
    kolList,
    kolListCount,
    kolGroupMemberList,
    kolGroupMemberListCount,
    updateKolListFilter,
    fetchKolList,
    fetchKolGroupMemberList,
  } = useFetchKolList();

  const { groupId, groupName } = groupData || {};
  const isKolGroup = groupId !== undefined;
  const isGroupPage = groupName !== undefined;
  const isKeywordAnalysisPage = isKolGroup && !isGroupPage;
  const list = isKolGroup ? kolGroupMemberList : kolList;
  const listCount = isKolGroup ? kolGroupMemberListCount : kolListCount;
  const fetchListFunc = isKolGroup ? fetchKolGroupMemberList : fetchKolList;

  const { navigateEntity } = useNavigateEntity();

  const lastFetchTime = useRef(new Date());

  const { page, orderData, search } = filter;

  const { headers, renderContents } = useGetKolTable();

  const selectedCount = selectedRowKolData.length;

  useEffect(() => {
    getKolList();
  }, [filter]);

  const abortControllerRef = useRef(new AbortController());

  const getKolList = async () => {
    abortControllerRef.current.abort(); // Cancel any ongoing request
    abortControllerRef.current = new AbortController(); // Create a new AbortController
    try {
      setIsLoading(true);
      const postFilter = {
        ...filter,
        order: filter?.orderData?.order,
        sortBy: filter?.orderData?.sort,
        verified: filter?.verified,
        xFollowers: filter?.xFollowers && {
          from: filter?.xFollowers[0],
          to: filter?.xFollowers[1],
        },
        categories: filter?.categories?.map((category) =>
          isObject(category) ? category.value : category
        ),
        etr: filter?.etr && {
          from: filter?.etr[0],
          to: filter?.etr[1],
        },
        etr: filter?.vtr && {
          from: filter?.vtr[0],
          to: filter?.vtr[1],
        },
        etr: filter?.originalTweetsRatio && {
          from: filter?.originalTweetsRatio[0],
          to: filter?.originalTweetsRatio[1],
        },
        language: filter?.language?.map((l) => (isObject(l) ? l.value : l)),
        location: filter?.location?.map((l) => (isObject(l) ? l.value : l)),
      };
      await fetchListFunc({
        entityName,
        filter: postFilter,
        signal: abortControllerRef.current.signal,
        groupId,
      });
      lastFetchTime.current = new Date();
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestSort = (event, property) => {
    const { sort, order } = orderData;
    const isAsc = sort === property && order === 1;
    updateKolListFilter({
      ...filter,
      page: 1,
      orderData: {
        sort: property,
        order: isAsc ? -1 : 1,
      },
    });
  };

  const deleteKolFromGroup = async () => {
    if (selectedCount === 0) return;

    try {
      confirm({
        title: formatMessage({ id: "kolGroup.deleteKol.title" }),
        content: formatMessage({ id: "kolGroup.deleteKol.description" }),
        confirmText: formatMessage({ id: "popup.confirmToDelete" }),
        cancelText: formatMessage({ id: "popup.confirmCancel" }),
      })
        .then(async () => {
          // await axios.delete(
          //   `${apiUrl}/api/kol/${entityName}/kolGroups/${groupId}`
          // );
          await getKolList();
          dispatchSnackbar({ message: "Delete Success", variant: "success" });
        })
        .catch(() => {});
    } catch (err) {
      dispatchSnackbar({ err });
    }
  };

  const getSelectedMenu = () => {
    const baseMenu = [
      {
        children: (
          <Box sx={{ color: "#fff" }}>
            {formatMessage(
              { id: "global.commonWord.selected" },
              { count: selectedCount }
            )}
          </Box>
        ),
      },
      {
        children: (
          <Box sx={{ height: "29px", width: "1px", backgroundColor: "#FFF" }} />
        ),
      },
    ];
    const selectedKolLibraryMenu = [
      {
        children: (
          <Box
            sx={{ cursor: "pointer", color: "#fff" }}
            onClick={() => {
              setOpenModal(ModalType.CREATE_GROUP);
            }}
          >
            {formatMessage({ id: "kol.bulk.createGroup.button" })}
          </Box>
        ),
      },
      {
        children: (
          <Box
            sx={{ cursor: "pointer", color: "#fff" }}
            onClick={() => {
              setOpenModal(ModalType.ADD_KOL);
            }}
          >
            {formatMessage({ id: "kol.bulk.addKoltoGroup.button" })}
          </Box>
        ),
      },
    ];
    const selectedKolGroupMenu = [
      {
        children: (
          <Box
            sx={{ cursor: "pointer", color: "#fff" }}
            onClick={deleteKolFromGroup}
          >
            {formatMessage({ id: "kolGroup.bulk.deleteKolFromGroup.button" })}
          </Box>
        ),
      },
    ];

    return [
      ...baseMenu,
      ...(!isKolGroup ? selectedKolLibraryMenu : []),
      ...(isGroupPage ? selectedKolGroupMenu : []),
    ];
  };

  const selectedMenu = getSelectedMenu();

  const hasFilter = useMemo(() => {
    const filterKeys = Object.keys(filter);
    return filterKeys.some((key) => {
      return (
        key !== "page" &&
        key !== "orderData" &&
        key !== "search" &&
        key !== "selectedKol"
      );
    });
  }, [filter]);
  const minusHeight = 203 + (remindBarShow ? 26 : 0) + (hasFilter ? 57 : 0);

  return (
    <Box>
      {isGroupPage && <Back noMt />}
      {filteredShowControls.title && (
        <Box
          display="flex"
          alignItems="flex-end"
          justifyContent="space-between"
          mb="24px"
          className="fade-up"
        >
          <div>
            <div className="title">
              {formatMessage(
                { id: pageTitleKey },
                {
                  groupName: groupName,
                }
              )}
            </div>

            {filteredShowControls.description && (
              <div className="description">
                {formatMessage({ id: pageDescriptionKey })}
              </div>
            )}
          </div>
        </Box>
      )}

      <div className="fade-up-more">
        <Box
          display="flex"
          alignItems="flex-end"
          mb="8px"
          justifyContent="space-between"
        >
          <Box className="item-flex">
            {filteredShowControls.search && (
              <div style={{ marginRight: 8 }}>
                <InputField
                  size={SIZE.MEDIUM}
                  onEnter={(v, e) => e.target.blur()}
                  placeholder={formatMessage({ id: "cdp.search.placeholder" })}
                  sx={{ width: "360px" }}
                  startAdornment={
                    <i
                      className="meta-crm-icon-ic_search font-size-16"
                      style={{ marginRight: 5, color: "#A5A5A5" }}
                    />
                  }
                  value={search}
                  onChange={(e) =>
                    updateKolListFilter({
                      ...filter,
                      search: e.target.value,
                      page: 1,
                    })
                  }
                  noBorder
                  onBlur={() => {
                    getKolList();
                    setSelectedRowKolData([]);
                  }}
                />
              </div>
            )}
            {filteredShowControls.filter && (
              <StyledFilterBtn
                onClick={() => {
                  setOpenModal(ModalType.FILTER);
                  setSelectedRowKolData([]);
                }}
              >
                <i className="meta-crm-icon-ic_filter2 font-size-14" />
                <Box ml="4px">{formatMessage({ id: "global.btn.filter" })}</Box>
              </StyledFilterBtn>
            )}
          </Box>
          <Box fontSize={12} color="#222" fontWeight={400}>
            {listCount} {formatMessage({ id: "global.unit.result" })}
          </Box>
        </Box>

        <FilterList
          hasFilter={hasFilter}
          selectedRowKolData={selectedRowKolData}
          openFilterModal={() => setOpenModal(ModalType.FILTER)}
        />
        <div style={{ position: "relative" }}>
          <CRMTable
            checkbox={isKeywordAnalysisPage ? false : true}
            containerStyle={{
              maxHeight: `calc(100vh - ${minusHeight}px)`,
            }}
            rowData={list}
            loadingIndicatorAmount={25}
            selectedRowData={selectedRowKolData}
            setSelectedRowData={setSelectedRowKolData}
            noDataTitle="No Kol"
            onRequestSort={handleRequestSort}
            orderBy={orderData.sort}
            order={_.get(orderData, "order") === 1 ? "asc" : "desc"}
            headers={headers}
            contents={_.map(list, (kol, i) => {
              return renderContents(kol);
            })}
            isLoading={isLoading}
            LoadingContent={<LoadingIcon />}
            noDataComponent={
              <EmptyIconStatus
                icon="table"
                wording="later"
                emptyTitle={formatMessage({
                  id: "emptyState.table.title",
                })}
                emptyDescription={formatMessage({
                  id: "emptyState.table.description",
                })}
              />
            }
            onRowClick={() => {}}
            page={page}
            setPage={(page) => updateKolListFilter({ ...filter, page })}
            dataCounts={listCount}
            leftFooter={
              <Box fontSize={12} color="#555">
                {formatMessage({ id: "kolModule.dataRefreshInfo" })}
              </Box>
            }
          />
        </div>
      </div>
      <TableMenu
        absolute
        show={selectedCount > 0}
        tabs={selectedMenu}
        style={{ zIndex: 100 }}
      />
      {/* modals */}
      <FilterModal
        open={openModal === "filter"}
        handleClose={() => setOpenModal("")}
      />
      <CreateKolGroupModal
        open={openModal === "createKolGroup"}
        handleClose={() => setOpenModal("")}
        entityName={entityName}
        choose={_.map(selectedRowKolData, "_id")}
      />
      <AddKolToGroupModal
        open={openModal === "addKoltoGroup"}
        handleClose={() => setOpenModal("")}
        entityName={entityName}
        choose={_.map(selectedRowKolData, "_id")}
      />
    </Box>
  );
};

export default KolTable;
